import { useCallback, useEffect, useState } from 'react';
import { Button } from '@heroui/react';

import TaskDialog from './TaskDialog';
import TaskBoard from './TaskBoard';
import Notice from '../../common/Notice';
import tasksService from '../../../services/tasks.service';
import { getUser } from '../../../services/session';
import { TASKS, messageOf } from '../../../i18n/el';

// Τα καθήκοντα της συλλογής (ΛΑ-18 έως ΛΑ-20), στην **καρτέλα** «Καθήκοντα».
//
// **Δικό της αίτημα, σε αντίθεση με την κάρτα των κρίσεων.** Εκείνη αθροίζει
// πλήθη που έρχονται ήδη με κάθε κάρτα πόρου· εδώ δεν υπάρχει τίποτα να
// αθροιστεί, γιατί το καθήκον **δεν κρέμεται από τον πόρο**: η σύνδεση είναι
// προαιρετική (§4.3.4), οπότε τα περισσότερα καθήκοντα δεν εμφανίζονται σε καμία
// κάρτα πόρου. Ο κατάλογος ζητείται ανά συλλογή, από τη διαδρομή του §4.5.1.
//
// **Τι άλλαξε στο Σετ Β.** Η κάρτα ήταν κατάλογος στην πλαϊνή στήλη· έγινε
// **πίνακας Kanban σε καρτέλα**, γιατί το §4.3.4 ονομάζει ρητά τη λογική Kanban.
// Η ίδια η ανάγνωση, το συμβόλαιο των props και ο διάλογος **δεν άλλαξαν**: ό,τι
// αφορά τη διάταξη ζει πλέον στο `TaskBoard`, που τον μοιράζεται με τη
// διασυλλογική οθόνη. Δύο αντίγραφα του ίδιου πίνακα θα απέκλιναν στη μία στήλη.

export default function CollectionTasks({
    collectionId,
    collection,
    canManage = false,
    resources = []
}) {
    const [tasks, setTasks] = useState([]);
    const [error, setError] = useState('');
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const currentUserId = getUser()?.id;
    const isArchived = collection?.archived === true;

    const load = useCallback(() => {
        if (!collectionId) return;

        tasksService.getTasks(collectionId).then(
            (page) => {
                // **Δεν εμπιστεύεται τη μορφή.** Ο κατάλογος μπορεί να λείπει σε
                // αποτυχία που δεν έφτασε ως απόρριψη, και τα `assignees`/`notes` να
                // είναι απόντα σε έγγραφο γραμμένο εκτός φόρμας. Προεπιλογή
                // παραμέτρου θα έπιανε μόνο το `undefined`.
                setTasks(Array.isArray(page?.data) ? page.data : []);
                setError('');
            },
            (apiError) => setError(messageOf(apiError))
        );
    }, [collectionId]);

    useEffect(load, [load]);

    function openCreate() {
        setEditing(null);
        setDialogOpen(true);
    }

    function openTask(task) {
        setEditing(task);
        setDialogOpen(true);
    }

    const closeDialog = () => setDialogOpen(false);

    const dismissError = () => setError('');

    /**
     * Ο ρόλος είναι **ένας για όλα τα καθήκοντα** εδώ: όλα ανήκουν σε αυτή τη
     * συλλογή. Στη διασυλλογική οθόνη δεν είναι, και γι' αυτό ο πίνακας δέχεται
     * συνάρτηση αντί για σημαία.
     */
    function canManageTask() {
        return canManage;
    }

    /**
     * Η **μία** διαδρομή αλλαγής κατάστασης, για τη μεταφορά και για τον
     * επιλογέα. Επιστρέφει την υπόσχεση, ώστε ο πίνακας να δείξει την αποτυχία
     * **δίπλα στην ενέργεια** αντί να αφήσει την κάρτα να γυρίσει σιωπηλά.
     */
    function changeStatus(task, status) {
        return tasksService.updateTask(task.id, { status }).then(
            load,
            (apiError) => Promise.reject(new Error(messageOf(apiError)))
        );
    }

    return (
        <section className="flex flex-col gap-4">
            {/* Το σφάλμα αποδίδεται **μέσα στην ενότητα**, δίπλα στην ενέργεια που
                το προκάλεσε, και ποτέ στην κορυφή της σελίδας: η σελίδα κυλά, οπότε
                ένα μήνυμα στην κορυφή της μπορεί να είναι εκτός οπτικού πεδίου τη
                στιγμή που ο χρήστης πατά το κουμπί. */}
            {error && (
                <Notice status="warning" title={TASKS.title} onDismiss={dismissError}>
                    {error}
                </Notice>
            )}

            {/* ΛΑ-18: μόνο ο συντονιστής δημιουργεί. Η απόκρυψη είναι **ευχρηστία
                και όχι ασφάλεια** — ο φράχτης είναι το `task:manage` του Πίνακα 1,
                στον εξυπηρετητή. Σε αρχειοθετημένη συλλογή το κουμπί λείπει, γιατί
                ο εξυπηρετητής θα απέρριπτε την ενέργεια (ΛΑ-07). */}
            {canManage && !isArchived && (
                <Button onPress={openCreate} size="sm" className="self-start">
                    {TASKS.create}
                </Button>
            )}

            {tasks.length === 0 ? (
                <p className="text-sm text-muted">
                    {canManage ? TASKS.emptyManage : TASKS.empty}
                </p>
            ) : (
                <TaskBoard
                    tasks={tasks}
                    currentUserId={currentUserId}
                    canManageOf={canManageTask}
                    onOpenTask={openTask}
                    onChangeStatus={changeStatus}
                    isReadOnly={isArchived}
                />
            )}

            {/* ⚠ **Υπό συνθήκη, και δεν είναι στιλιστική επιλογή.** Το `isOpen` του
                διαλόγου γίνεται ψευδές από πηγή **εκτός** του — μετά από επιτυχή
                αποθήκευση ή διαγραφή, δηλαδή από τον γονέα. Το `Modal.Root` του
                HeroUI κλείνει μόνο όταν το κλείσιμο το ξεκινά το ίδιο· χωρίς την
                αποπροσάρτηση ο διάλογος **δεν κλείνει ποτέ**. */}
            {isDialogOpen && (
                <TaskDialog
                    task={editing}
                    collectionId={collectionId}
                    collection={collection}
                    resources={resources}
                    canManage={canManage}
                    currentUserId={currentUserId}
                    onClose={closeDialog}
                    onSaved={handleSaved}
                />
            )}
        </section>
    );

    /**
     * Ο χειριστής δίνεται ως **όνομα** και όχι ως ενσωματωμένο βέλος.
     *
     * Δεν είναι στιλ: ο στατικός φράχτης καλωδίωσης διαβάζει τα χαρακτηριστικά ενός
     * στοιχείου ως κείμενο μέχρι το πρώτο `>`, οπότε ένα `onSaved={() => …}` **κόβει
     * τη σάρωση στο βέλος** και κάθε επόμενο prop φαίνεται να λείπει. Ο φράχτης το
     * ανέφερε· η φθηνή απάντηση θα ήταν να αγνοηθεί, και φράχτης που αγνοείται μια
     * φορά παύει να διαβάζεται.
     */
    function handleSaved() {
        setDialogOpen(false);
        load();
    }
}
